import { Ref, useImperativeHandle } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm } from 'react-hook-form';

import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqImage } from '../fields/rfq-image';
import { RfqAnonymousLoginIllustration } from '../images/rfq-anonymous-login-illustration';

export interface FormValue {
   email: string;
}

interface NewProps {
   stepRef: Ref<RequestForQuotationStep>;
   onSubmit: (value: FormValue) => void;
   defaultFormValue: FormValue | undefined;
}

const emailRegex = /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z]{2,4}$/;

export const RfqFlyoutStepVerifyEmail = ({ stepRef, defaultFormValue, onSubmit }: NewProps) => {
   const translate = useTranslations();

   const { control, handleSubmit } = useForm<FormValue>({
      defaultValues: defaultFormValue ?? {
         email: ''
      }
   });

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((v) => onSubmit(v))();
      }
   }));

   return (
      <Box direction="column">
         <RfqImage>
            <RfqAnonymousLoginIllustration />
         </RfqImage>
         <Typography component="span" variant="body" color="strong">
            {translate('page-consumer.request-for-quotation.verify-email-description')}
         </Typography>
         <Controller
            control={control}
            name="email"
            rules={{
               required: translate('form-validation.required', { field: translate('form-field.email') }),
               pattern: {
                  value: emailRegex,
                  message: translate('form-validation.invalid-email')
               }
            }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
               <InputField
                  inputRef={ref}
                  {...field}
                  type="email"
                  label={translate('page-consumer.request-for-quotation.contact-info.email-address')}
                  id="rfq-verify-email-address-input"
                  error={!!error}
                  errorMessage={error?.message}
               />
            )}
         />
      </Box>
   );
};
